/**
 * Main Application Component
 * 
 * This is the root component of the Bank Automation Admin Dashboard.
 * It provides routing and navigation for managing bank accounts, 
 * projects, and parser keys.
 * 
 * The dashboard sends requests to the APLA module running on Windows
 * with ADSPower, which manages Selenium workers for bank automation.
 */
import React from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Layout, Menu, Button } from 'antd';
import BankPage from './pages/BankPage/BankPage';
import ProjectsPage from './pages/ProjectsPage/ProjectsPage';
import KeysPage from './pages/KeysPage/KeysPage';
import LoginPage from './pages/LoginPage/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import AccountDetailsPage from './pages/AccountDetailsPage/AccountDetailsPage';
import { getUserRole } from './services/api';
import BatchList from './pages/BatchPage/BatchList';
import BatchCreate from './pages/BatchPage/BatchCreate';
import BatchDetails from './pages/BatchPage/BatchDetails';
import FundRules from './pages/FundPage/FundRules';
import FundMonitor from './pages/FundPage/FundMonitor';
import ManualTransfer from './pages/ManualPage/ManualTransfer';
import BotSettings from './pages/BotSettingsPage/BotSettings';
import LogsPage from './pages/LogsPage/LogsPage';
import './App.css';

const { Header, Content } = Layout;

/**
 * Application Header Component
 * 
 * Renders the navigation menu with links to main sections:
 * - Accounts: Main bank accounts management page
 * - Projects: Project configuration for callback URLs (Admin only)
 * - Parser Keys: API keys for parsing bank statements (Admin only)
 * 
 * Includes logout functionality that clears JWT token from session storage.
 */
const AppHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const role = getUserRole();
  const isAxisOperator = role === 'AXIS_OPERATOR';

  // Don't show header on login page
  if (location.pathname === '/login') return null;

  /**
   * Determines which menu item should be highlighted based on current path
   * @returns {string} The key of the currently active menu item
   */
  const selectedKey = () => {
    if (location.pathname.startsWith('/projects')) return 'projects';
    if (location.pathname.startsWith('/keys')) return 'keys';
    if (location.pathname.startsWith('/batch-payouts')) return 'batch-payouts';
    if (location.pathname.startsWith('/fund-management')) return 'fund-management';
    if (location.pathname.startsWith('/transfers')) return 'transfers';
    if (location.pathname.startsWith('/settings/bot')) return 'bot-settings';
    if (location.pathname.startsWith('/logs')) return 'logs';
    return 'accounts';
  };

  /**
   * Handles menu item clicks and navigates to corresponding routes
   * @param {Object} param0 - Object containing the clicked menu item key
   */
  const handleMenuClick = ({ key }) => {
    switch (key) {
      case 'accounts':
        navigate('/');
        break;
      case 'projects':
        navigate('/projects');
        break;
      case 'keys':
        navigate('/keys');
        break;
      case 'batch-payouts':
        navigate('/batch-payouts');
        break;
      case 'fund-management':
        navigate('/fund-management/monitor');
        break;
      case 'transfers':
        navigate('/transfers/manual');
        break;
      case 'bot-settings':
        navigate('/settings/bot');
        break;
      case 'logs':
        navigate('/logs');
        break;
      case 'logout':
        // Clear JWT token and redirect to login
        sessionStorage.removeItem('jwtToken');
        navigate('/login');
        break;
      default:
        break;
    }
  };

  return (
    <Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Menu
        theme="dark"
        mode="horizontal"
        selectedKeys={[selectedKey()]}
        onClick={handleMenuClick}
        style={{ flex: 1 }}
      >
        <Menu.Item key="accounts">Accounts</Menu.Item>
        {!isAxisOperator && <Menu.Item key="projects">Projects</Menu.Item>}
        {!isAxisOperator && <Menu.Item key="keys">Parser Keys</Menu.Item>}
        {!isAxisOperator && <Menu.Item key="batch-payouts">Batch Payouts</Menu.Item>}
        {!isAxisOperator && <Menu.Item key="fund-management">Fund Management</Menu.Item>}
        {!isAxisOperator && <Menu.Item key="transfers">Manual Transfers</Menu.Item>}
        {!isAxisOperator && <Menu.Item key="bot-settings">Bot Settings</Menu.Item>}
        {!isAxisOperator && <Menu.Item key="logs">Logs</Menu.Item>}
      </Menu>
      <Button type="primary" danger onClick={() => handleMenuClick({ key: 'logout' })}>
        Logout
      </Button>
    </Header>
  );
};

/**
 * Main App Component
 * 
 * Sets up React Router with the following routes:
 * - /login: Public login page
 * - /: Protected bank accounts list (BankPage)
 * - /projects: Protected projects management
 * - /keys: Protected parser keys management
 * - /account/:id: Protected account details/edit page
 * - *: Redirect unknown routes to login
 */
const App = () => {
  return (
    <Router>
      <Layout style={{ minHeight: '100vh' }}>
        <AppHeader />
        <Content style={{ padding: '24px', background: '#fff' }}>
          <Routes>
            {/* Public route - Login page */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* Protected route - Bank accounts management (main page) */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <BankPage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected route - Projects management */}
            <Route
              path="/projects"
              element={
                <ProtectedRoute>
                  <ProjectsPage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected route - Parser keys management */}
            <Route
              path="/keys"
              element={
                <ProtectedRoute>
                  <KeysPage />
                </ProtectedRoute>
              }
            />
            
            {/* Protected route - Batch Payouts */}
            <Route
              path="/batch-payouts"
              element={
                <ProtectedRoute>
                  <BatchList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/batch-payouts/create"
              element={
                <ProtectedRoute>
                  <BatchCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/batch-payouts/:id"
              element={
                <ProtectedRoute>
                  <BatchDetails />
                </ProtectedRoute>
              }
            />

            {/* Protected route - Fund Management */}
            <Route
              path="/fund-management/rules"
              element={
                <ProtectedRoute>
                  <FundRules />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fund-management/monitor"
              element={
                <ProtectedRoute>
                  <FundMonitor />
                </ProtectedRoute>
              }
            />

            {/* Protected route - Manual Transfers */}
            <Route
              path="/transfers/manual"
              element={
                <ProtectedRoute>
                  <ManualTransfer />
                </ProtectedRoute>
              }
            />

            {/* Protected route - Bot Settings */}
            <Route
              path="/settings/bot"
              element={
                <ProtectedRoute>
                  <BotSettings />
                </ProtectedRoute>
              }
            />

            {/* Protected route - Individual account details and editing */}
            <Route
              path="/account/:id"
              element={
                <ProtectedRoute>
                  <AccountDetailsPage />
                </ProtectedRoute>
              }
            />

            {/* Protected route - Automation Logs */}
            <Route
              path="/logs"
              element={
                <ProtectedRoute>
                  <LogsPage />
                </ProtectedRoute>
              }
            />
            
            {/* Fallback - redirect unknown routes to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Content>
      </Layout>
    </Router>
  );
};

export default App;
